let cachedCookies = null;
const COOKIE_CACHE_KEY = "locaso:cache:cookies:v1";
const COOKIE_CACHE_TTL_MS = 60 * 1000;

const readSessionCache = (key) => {
  try {
    const rawValue = sessionStorage.getItem(key);
    if (!rawValue) {
      return null;
    }

    const parsedValue = JSON.parse(rawValue);
    if (!parsedValue || Date.now() > parsedValue.expiresAt) {
      sessionStorage.removeItem(key);
      return null;
    }

    return parsedValue.data;
  } catch (error) {
    return null;
  }
};

const writeSessionCache = (key, data, ttlMs) => {
  try {
    const payload = {
      data,
      expiresAt: Date.now() + ttlMs,
    };
    sessionStorage.setItem(key, JSON.stringify(payload));
  } catch (error) {}
};

const clearSessionCache = (key) => {
  try {
    sessionStorage.removeItem(key);
  } catch (error) {}
};

// Global request tracker to prevent duplicate in-flight calls
const inflightRequests = {};
const globalRegistry = {
  categories: null,
  addresses: null,
};

export default class Util {
  getCookie = (name) => {
    const allCookies = this.getAllCookies();
    if (
      allCookies &&
      Object.prototype.hasOwnProperty.call(allCookies, name)
    ) {
      return allCookies[name];
    }

    // Fallback: if not in cache, return null (cookie not set)
    // The cache is pre-warmed on page load via preloadCookies()
    return null;
  };

  /**
   * Returns cached cookies synchronously. On first call (before cache is warm),
   * returns {} and triggers a background fetch. Use preloadCookies() early in
   * the page lifecycle to ensure the cache is warm before any reads.
   */
  getAllCookies = () => {
    if (cachedCookies) {
      return cachedCookies;
    }

    const cachedSessionCookies = readSessionCache(COOKIE_CACHE_KEY);
    if (cachedSessionCookies) {
      cachedCookies = cachedSessionCookies;
      return cachedCookies;
    }

    // Cache miss — trigger async fetch (callers get {} on first call)
    // This should rarely happen if preloadCookies() was called on page init
    this.preloadCookies();
    return cachedCookies || {};
  };

  /**
   * Async preload — call this once early in page lifecycle.
   * After this resolves, getAllCookies() returns from cache synchronously.
   */
  preloadCookies = () => {
    if (cachedCookies) return Promise.resolve(cachedCookies);

    const cachedSessionCookies = readSessionCache(COOKIE_CACHE_KEY);
    if (cachedSessionCookies) {
      cachedCookies = cachedSessionCookies;
      return Promise.resolve(cachedCookies);
    }

    // Deduplicate in-flight requests
    if (inflightRequests["cookies"]) {
      return inflightRequests["cookies"];
    }

    const fetchPromise = new Promise((resolve) => {
      $.ajax({
        url: globalThis.baseURL + "api/customer/CustomerCookies/getAllSessionValues",
        type: "GET",
        success: (result) => {
          cachedCookies = result;
          writeSessionCache(COOKIE_CACHE_KEY, result, COOKIE_CACHE_TTL_MS);
          delete inflightRequests["cookies"];
          resolve(cachedCookies);
        },
        error: (err) => {
          console.error("Locaso: Failed to fetch session cookies", err);
          cachedCookies = {};
          delete inflightRequests["cookies"];
          resolve(cachedCookies);
        },
      });
    });

    inflightRequests["cookies"] = fetchPromise;
    return fetchPromise;
  };

  setCookies = (cookieMap) => {
    if (!cookieMap || typeof cookieMap !== "object") return Promise.resolve(null);

    // Smart Check: Only hit the server if the data is different from our cache
    const currentCookies = this.getAllCookies();
    const hasChanges = Object.keys(cookieMap).some(key => {
        return String(cookieMap[key]) !== String(currentCookies[key]);
    });

    if (!hasChanges) {
        return Promise.resolve({ status: true, message: 'No changes needed' });
    }

    return new Promise((resolve) => {
      $.ajax({
        url: globalThis.baseURL + "api/customer/CustomerCookies/setSessionId",
        type: "POST",
        data: {
          data: cookieMap,
        },
        success: (result) => {
          // Clear caches immediately on change
          clearSessionCache(COOKIE_CACHE_KEY);
          cachedCookies = null;
          resolve(result);
        },
        error: (err) => {
          clearSessionCache(COOKIE_CACHE_KEY);
          cachedCookies = null;
          resolve(null);
        },
      });
    });
  };

  createCookie = (name, value, days) => {
    const data = {};
    data[name] = value;
    return this.setCookies(data);
  };

  eraseCookie = (name) => {
    return new Promise((resolve) => {
      $.ajax({
        url: globalThis.baseURL + "api/customer/CustomerCookies/unsetSessionValues",
        type: "POST",
        data: {
          unset: name,
        },
        success: (result) => {
          // Clear caches immediately on change
          clearSessionCache(COOKIE_CACHE_KEY);
          cachedCookies = null;
          resolve(result);
        },
        error: (err) => {
          clearSessionCache(COOKIE_CACHE_KEY);
          cachedCookies = null;
          resolve(null);
        },
      });
    });
  };

  getPhpCookies = () => {
    return this.getAllCookies();
  };

  distance = (lat1, lon1, lat2, lon2, unit) => {
    if (lat1 == lat2 && lon1 == lon2) {
      return 0;
    } else {
      var radlat1 = (Math.PI * lat1) / 180;
      var radlat2 = (Math.PI * lat2) / 180;
      var theta = lon1 - lon2;
      var radtheta = (Math.PI * theta) / 180;
      var dist =
        Math.sin(radlat1) * Math.sin(radlat2) +
        Math.cos(radlat1) * Math.cos(radlat2) * Math.cos(radtheta);
      if (dist > 1) {
        dist = 1;
      }
      dist = Math.acos(dist);
      dist = (dist * 180) / Math.PI;
      dist = dist * 60 * 1.1515;
      dist = dist * 1.609344;
      return dist;
    }
  };

  checkLocalStorage = (key) => {
    if (localStorage.getItem(key) == null) {
      return null;
    } else {
      return localStorage.getItem(key);
    }
  };

  setRedirectAfterLogin = (url) => {
    localStorage.setItem("REDIRECT_AFTER_LOGIN", url);
  };

  getRedirectAfterLogin = () => {
    const url = localStorage.getItem("REDIRECT_AFTER_LOGIN");
    localStorage.removeItem("REDIRECT_AFTER_LOGIN");
    return url;
  };

  /**
   * Unified loader for categories to prevent duplicate calls
   */
  getCategories = (successCallback, forceRefresh = false) => {
    if (!forceRefresh && globalRegistry.categories) {
      if (typeof successCallback === "function")
        successCallback(globalRegistry.categories);
      return Promise.resolve(globalRegistry.categories);
    }

    // Check if a request is already in flight
    if (inflightRequests["categories"]) {
      return inflightRequests["categories"].then((res) => {
        if (typeof successCallback === "function") successCallback(res);
        return res;
      });
    }

    const ajaxPromise = new Promise((resolve, reject) => {
      $.ajax({
        url: globalThis.baseURL + "api/customer/Homepage/getAllCategory",
        type: "GET",
        headers: { Authorization: globalThis.X_API_KEY },
        success: (result) => {
          globalRegistry.categories = result;
          delete inflightRequests["categories"];
          if (typeof successCallback === "function") successCallback(result);
          resolve(result);
        },
        error: (err) => {
          delete inflightRequests["categories"];
          reject(err);
        },
      });
    });

    inflightRequests["categories"] = ajaxPromise;
    return ajaxPromise;
  };

  /**
   * Location-aware categories loader — only returns categories
   * that have vendors within (buffer) km of (lat, lng).
   */
  getCategoriesWithLocation = (lat, lng, buffer, successCallback) => {
    // Build a cache key based on rounded location so it invalidates on move
    const locKey = `${Math.round(lat * 100)}:${Math.round(lng * 100)}:${buffer}`;
    const cacheKey = `categories_loc_${locKey}`;

    if (globalRegistry[cacheKey]) {
      if (typeof successCallback === "function")
        successCallback(globalRegistry[cacheKey]);
      return Promise.resolve(globalRegistry[cacheKey]);
    }

    if (inflightRequests[cacheKey]) {
      return inflightRequests[cacheKey].then((res) => {
        if (typeof successCallback === "function") successCallback(res);
        return res;
      });
    }

    const ajaxPromise = new Promise((resolve, reject) => {
      $.ajax({
        url: baseURL + "api/customer/Homepage/getCategoriesWithLocation",
        type: "POST",
        data: { lat, lon: lng, buffer },
        headers: { Authorization: X_API_KEY },
        success: (result) => {
          const data = Array.isArray(result) ? result : [];
          globalRegistry[cacheKey] = data;
          delete inflightRequests[cacheKey];
          if (typeof successCallback === "function") successCallback(data);
          resolve(data);
        },
        error: (err) => {
          delete inflightRequests[cacheKey];
          if (typeof successCallback === "function") successCallback([]);
          resolve([]);
        },
      });
    });

    inflightRequests[cacheKey] = ajaxPromise;
    return ajaxPromise;
  };

  /**
   * Unified loader for customer addresses
   */
  getAddresses = (customerId, successCallback, forceRefresh = false) => {
    if (!customerId) {
      const cookies = this.getAllCookies();
      customerId = cookies ? cookies.CUSTOMER_ID : null;
    }

    if (!customerId) return Promise.resolve([]);

    if (!forceRefresh && globalRegistry.addresses) {
      if (typeof successCallback === "function")
        successCallback(globalRegistry.addresses);
      return Promise.resolve(globalRegistry.addresses);
    }

    if (inflightRequests["addresses"]) {
      return inflightRequests["addresses"].then((res) => {
        if (typeof successCallback === "function") successCallback(res);
        return res;
      });
    }

    const ajaxPromise = new Promise((resolve, reject) => {
      $.ajax({
        url: globalThis.baseURL + "api/customer/Account/getAddress",
        type: "POST",
        data: { Customer_ID: customerId },
        headers: { Authorization: globalThis.X_API_KEY },
        success: (result) => {
          globalRegistry.addresses = result;
          delete inflightRequests["addresses"];
          if (typeof successCallback === "function") successCallback(result);
          resolve(result);
        },
        error: (err) => {
          delete inflightRequests["addresses"];
          reject(err);
        },
      });
    });

    inflightRequests["addresses"] = ajaxPromise;
    return ajaxPromise;
  };

  /**
   * Global Toast Trigger
   * @param {string} message The message to show
   * @param {string} type 'success', 'danger', 'info', 'dark' etc. (Bootstrap color suffix)
   */
  showToast = (message, type = "dark") => {
    console.log(`Locaso: Triggering toast [${message}] with type [${type}]`);
    const toastEl = document.getElementById("locasoToast");
    if (!toastEl) {
      console.warn("Locaso: Toast element #locasoToast not found in DOM!");
      return;
    }

    const toastBody = toastEl.querySelector(".toast-body");
    if (toastBody) {
      toastBody.innerText = message;
    } else {
      console.warn("Locaso: .toast-body not found inside #locasoToast!");
    }

    // Update color and icon
    toastEl.className = `toast align-items-center text-white border-0 rounded-4 shadow-lg locaso-premium-toast bg-${type}`;
    
    const iconEl = toastEl.querySelector(".toast-icon-wrap i");
    if (iconEl) {
      if (type === "success") iconEl.className = "fa-solid fa-circle-check fs-5";
      else if (type === "danger") iconEl.className = "fa-solid fa-circle-exclamation fs-5";
      else iconEl.className = "fa-solid fa-circle-info fs-5";
    }

    try {
      const bootstrapObj = window.bootstrap || globalThis.bootstrap;
      if (bootstrapObj && bootstrapObj.Toast) {
        const toast = bootstrapObj.Toast.getOrCreateInstance(toastEl);
        toast.show();
        console.log("Locaso: Bootstrap toast.show() called successfully.");
      } else {
        console.error("Locaso: Bootstrap.Toast is NOT defined!", bootstrapObj);
        // Fallback: manually add 'show' class if bootstrap JS is missing
        toastEl.classList.add("show");
        setTimeout(() => toastEl.classList.remove("show"), 3000);
      }
    } catch (err) {
      console.error("Locaso: Error initializing bootstrap toast:", err);
    }
  };
}
